import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'dark' | 'light' | 'system';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    setTheme: (theme: Theme) => void;
    effectiveTheme: 'dark' | 'light';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setThemeState] = useState<Theme>(() => {
        const savedTheme = localStorage.getItem('admin-theme');
        return (savedTheme as Theme) || 'dark';
    });

    // Track system theme to force re-renders
    const [systemTheme, setSystemTheme] = useState<'dark' | 'light'>(() => {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    // Get the effective theme (resolving 'system' to actual theme)
    const getEffectiveTheme = (themeValue: Theme): 'dark' | 'light' => {
        if (themeValue === 'system') {
            return systemTheme;
        }
        return themeValue;
    };

    useEffect(() => {
        // Save theme preference
        localStorage.setItem('admin-theme', theme);

        // Listen for system theme changes if system theme is selected
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
            const newSystemTheme = e.matches ? 'dark' : 'light';
            setSystemTheme(newSystemTheme);
        };
        
        // Initial check
        handleChange(mediaQuery);
        
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    const toggleTheme = () => {
        setThemeState(prevTheme => {
            if (prevTheme === 'dark') return 'light';
            if (prevTheme === 'light') return 'system';
            return 'dark';
        });
    };

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
    };

    // Provide both the theme preference and the effective theme
    const effectiveTheme = getEffectiveTheme(theme);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, effectiveTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
